// File: CustomTemp.jsx
import React, { useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const availableFields = [
  'PatientName', 'Age', 'Phone', 'Gender', 'PatientId'
];

const defaultColumns = [
  { label: 'Test Name', field: 'TestName', order: 1, visible: true },
  { label: 'Result', field: 'Result', order: 2, visible: true },
  { label: 'Reference Range', field: 'ReferenceValue', order: 3, visible: true },
  { label: 'Unit', field: 'Unit', order: 4, visible: true }
];

const CustomTemp = () => {
  const [templateName, setTemplateName] = useState('');
  const [headerHtml, setHeaderHtml] = useState('<h2>Report for {{PatientName}}</h2>');
  const [footerHtml, setFooterHtml] = useState('<p>Doctor Signature: __________</p>');
  const [headerBgColor, setHeaderBgColor] = useState('#ffffff');
  const [footerBgColor, setFooterBgColor] = useState('#ffffff');
  const [tableStyle, setTableStyle] = useState('width: 100%; border: 1px solid black;');
  const [tableBgColor, setTableBgColor] = useState('#ffffff');
  const [columns, setColumns] = useState(defaultColumns);

  const updateColumn = (index, key, value) => {
    const updated = [...columns];
    updated[index][key] = value;
    setColumns(updated);
  };

  const insertField = (field, target, setter) => {
    const updated = `${target} {{${field}}}`;
    setter(updated);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedCols = Array.from(columns);
    const [removed] = updatedCols.splice(result.source.index, 1);
    updatedCols.splice(result.destination.index, 0, removed);
    setColumns(updatedCols.map((col, i) => ({ ...col, order: i + 1 })));
  };

  const saveTemplate = async () => {
    const payload = {
      name: templateName,
      headerHtml,
      footerHtml,
      body: {
        tableStyle: `${tableStyle}; background-color: ${tableBgColor};`,
        columns,
        headerBgColor,
        footerBgColor
      }
    };

    try {
      const response = await axios.post('https://localhost:44397/api/Template', payload);
      if(response){
        alert('Template saved successfully!');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save template.');
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-xl font-bold mb-2">Lab Report Template Builder</h2>

      <input
        className="border p-2 w-full mb-3"
        placeholder="Template Name"
        value={templateName}
        onChange={(e) => setTemplateName(e.target.value)}
      />

      <div className="mb-4">
        <h3 className="font-bold mb-2">Header Section</h3>
        <label className="block mb-1">Header Background</label>
        <input
          type="color"
          value={headerBgColor}
          onChange={(e) => setHeaderBgColor(e.target.value)}
          className="mb-2 w-16 h-8 border"
        />
        <ReactQuill value={headerHtml} onChange={setHeaderHtml} />
        <div className="mt-2 flex flex-wrap gap-2">
          {availableFields.map((field, i) => (
            <button
              key={i}
              className="bg-gray-200 px-2 py-1 text-sm rounded"
              onClick={() => insertField(field, headerHtml, setHeaderHtml)}
            >
              {`+ {{${field}}}`}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <h3 className="font-bold mb-2">Footer Section</h3>
        <label className="block mb-1">Footer Background</label>
        <input
          type="color"
          value={footerBgColor}
          onChange={(e) => setFooterBgColor(e.target.value)}
          className="mb-2 w-16 h-8 border"
        />
        <ReactQuill value={footerHtml} onChange={setFooterHtml} />
        <div className="mt-2 flex flex-wrap gap-2">
          {availableFields.map((field, i) => (
            <button
              key={i}
              className="bg-gray-200 px-2 py-1 text-sm rounded"
              onClick={() => insertField(field, footerHtml, setFooterHtml)}
            >
              {`+ {{${field}}}`}
            </button>
          ))}
        </div>
      </div>

      <textarea
        className="border p-2 w-full mb-2"
        rows="2"
        placeholder="Table Style"
        value={tableStyle}
        onChange={(e) => setTableStyle(e.target.value)}
      />

      <div className="mb-4">
        <label className="block mb-1 font-medium">Table Background Color</label>
        <input
          type="color"
          value={tableBgColor}
          onChange={(e) => setTableBgColor(e.target.value)}
          className="w-16 h-8 border"
        />
      </div>

      <h3 className="font-semibold mt-4 mb-2">Table Columns (Drag to Reorder)</h3>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="columns">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="min-h-[50px]">
              {columns.map((col, i) => (
                <Draggable
                  key={`col-${col.field}-${i}`}
                  draggableId={`col-${col.field}-${i}`}
                  index={i}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="border p-2 my-2 bg-white shadow-sm rounded"
                    >
                      <input
                        className="border p-1 mr-2"
                        value={col.label}
                        onChange={(e) => updateColumn(i, 'label', e.target.value)}
                        placeholder="Label"
                      />
                      <select
                        value={col.field}
                        onChange={(e) => updateColumn(i, 'field', e.target.value)}
                        className="border p-1 mr-2"
                      >
                        <option value="TestName">TestName</option>
                        <option value="Result">Result</option>
                        <option value="ReferenceValue">ReferenceValue</option>
                        <option value="Unit">Unit</option>
                      </select>
                      <input
                        className="border p-1 mr-2 w-20"
                        type="number"
                        value={col.order}
                        onChange={(e) => updateColumn(i, 'order', +e.target.value)}
                      />
                      <label>
                        <input
                          type="checkbox"
                          checked={col.visible}
                          onChange={(e) => updateColumn(i, 'visible', e.target.checked)}
                        /> Show
                      </label>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <button onClick={saveTemplate} className="bg-blue-600 text-white px-4 py-2 rounded mt-4">
        Save Template
      </button>
    </div>
  );
};

export default CustomTemp;
